import { inject, Injectable, Injector } from "@angular/core";
import { PatientDocumentClient } from "../../common/services/patient-document.client";
import {
  Activity,
  EndpointInfo,
  EpocData,
  Lookup,
  NavigationStep,
  Operator,
  Parameter,
  Question,
  Questionnaire,
  Section,
  SourceData,
  SummaryCondition,
} from "./epoc.interface";
import { BehaviorSubject, firstValueFrom, map } from "rxjs";
import { LookupClient } from "src/app/common/services/lookup.client";
import { environment } from "environment";
import { HttpClient } from "@angular/common/http";
import { Constants, SUMMARY_DEPENDENCY_ACTION } from "./util/constants";
import { NavigationSteps, DisabledSectionIdsForEvalutionOnly, QuestionIds, SummaryPlaceholders } from "src/app/common/constants/documentation-constants";
import { AppConstants } from "src/app/common/constants/app-constants";
import { QuestionTypes } from "./epoc-question-field/epoc-question-field-constants";
import { DisciplineTitle } from "src/app/common/enums/discipline.enum";

@Injectable({ providedIn: "root" })
export class EpocService {
  composeSection: Section | null = {} as Section;
  questionnaire: Questionnaire | null = {} as Questionnaire;
  document!: EpocData | null;
  isNarrativeMandatory: boolean = false;
  recentResponseDocument!: EpocData;
  documentMetadata: any;
  summaryInfo: any = {};
  allQuestions: Question[] = [];
  currentStep: NavigationStep | null = {} as NavigationStep;
  constructor(private patientDocumentClient: PatientDocumentClient, private httpClient: HttpClient, private injector: Injector) { }

  public storeRowValues$ = new BehaviorSubject<any>(null);
  storeRowValueOptions = this.storeRowValues$.asObservable();

  public setStoreValues(storeOptions: any) {
    this.storeRowValues$.next(storeOptions);
  }

  activitySTG: any[] = [];
  activityLTG: any[] = [];
  endpointsStore: any = {};
  multiDependencyStore: any = {};
  userDiscipline: any;
  questionOptions: any[] = [];

  activityDateForDocument!: any;
  socDateForTherapy!: any;

  originalActivityValues: any = {};

  refetchSections: any = {};
  skilledCareDynamicRadio2Options: any[] = [];

  getActivityDate(patientDocumentId: any) {
    return this.httpClient.get<{ responseData: any }>(
      `${environment.endpoint}/patientdocumentation/get-extra-documentation-info`, {
      params: {
        patientDocumentId: patientDocumentId
      }
    }
    ).pipe(
      map((response) => {
        return response.responseData;
      })
    );
  }

  getTitle(title: string) {
    if (Constants.AddGoalButtonConfig.text === title) {
      return "added Goals"
    }
    if (Constants.UpdateGoalButtonConfig.text === title) {
      return "Updated Goals"
    }
    if (Constants.AddNewSTG.text === title) {
      return "added STG"
    }
    if (Constants.AddNewLTG.text === title) {
      return "added LTG"
    }
    if (Constants.UpdateSTG.text === title) {
      return "Updated STG"
    }
    if (Constants.UpdateLTG.text === title) {
      return "Updated LTG"
    }
    if (Constants.NewSTGGoalButtonConfig.text === title) {
      return "added new STG"
    }
    if (Constants.NewLTGGoalButtonConfig.text === title) {
      return "added new LTG"
    }
    return title
  }

  async getQuestionOptions() {
    const allQuestions = this.getAllQuestions();
    const lookup = this.injector.get(LookupClient);
    const promises = allQuestions.map(async (question) => {
        if (question.LookupInfo && 
            !question.QuestionId.includes('FunctionalLimitations') && 
            !question.QuestionId.includes('UnderlyingImpairments') &&
            !question.QuestionId.includes('DiagnosticGroup')) {
            try {

              const lookupValues = await firstValueFrom(lookup.getValuesByLookupId(
                question.LookupInfo,
                this.documentMetadata.facilityId,
                question.LookupInfo.isCustomized,
                this.documentMetadata.therapyId
            ));
            
              let options: any[] = [];

              if (question.QuestionType.includes(QuestionTypes.DynamicSubHead_NewApproaches)) {
                lookupValues?.forEach((aof: any) => {
                  aof.reason?.forEach((r: any) => {
                    options.push({
                      Id: r.reasonId,
                      Value: r.reason,
                      IsBlankRequired: false,
                      IsMultiSelect: false,
                      areaOfFocusId: aof.areaOfFocusId,
                      areaOfFocus: aof.areaOfFocus
                    });
                  });
                });
              }
              else if(question.QuestionType.includes(QuestionTypes.SkilledCare_EnhancedTag)) {
                options = lookupValues; 
                const subOptions = await firstValueFrom(lookup.getValuesByLookupId(
                  question.SubLookupInfo,
                  this.documentMetadata.facilityId,
                  question.LookupInfo.isCustomized,
                  this.documentMetadata.therapyId
              ));
              question.SubOptions = subOptions;
              }
              // Case 2: Default mapping
              else {
                options = lookupValues.map((o) => ({
                    Id: o.id !== null ? Number(o.id) : 0,  // Default to 0 if null
                    Value: o.name,
                }));
              }
              question.Options = options;
                this.questionOptions.push({ question: question.QuestionId, options: question.Options });
            } catch (error) {
                console.error(`Error fetching options for question ${question.QuestionId}:`, error);
            }
        }
    });
    await Promise.all(promises);
  }

  setQuestionOptions(questionId: string, options: any[]): void {
    const existing = this.questionOptions.find(q => q.question === questionId);
  
    if (existing) {
      existing.options = options;
    } else {
      this.questionOptions.push({
        question: questionId,
        options
      });
    }
  }

  getEpocSectionMetadata() {
    return {
      MedicalInformation_GeneralInformation: {
        description: "",
        historyRoute: "",
        composeActionRoute: "",
      },
      MedicalInformation_PriorHospitalization: {
        description: "",
        historyRoute: "",
        composeActionRoute: "",
      },
      MedicalInformation_ReasonForReferral: {
        description: "",
        historyRoute: "",
        composeActionRoute: "",
      },
      UnderlyingImpairments_UnderlyingImpairments: {
        description: "",
        historyRoute: "",
        composeActionRoute: "",
      },
    };
  }

  getEpocSectionCopyData(section: Section): SourceData[] {
    if (
      section?.SectionId ===
      "MedicalInformation_MedicationsImpactingTreatmentPlan"
    ) {
      const lorem =
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean euismod bibendum laoreet. Proin gravida dolor sit amet lacus accumsan et viverra justo commodo.  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean euismod bibendum laoreet. Proin gravida dolor sit amet lacus accumsan et viverra justo commodo";
      return [
        {
          disciplineid: 1,
          history: [
            {
              socEvalDate: "2023-07-01T00:00:00",
              eocDate: null,
              patientDocumentId: 67,
              historyValue: `{\r\n  \"Notes\": \"Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean euismod bibendum laoreet. Proin gravida dolor sit amet lacus accumsan et viverra justo commodo.  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean euismod bibendum laoreet. Proin gravida dolor sit amet lacus accumsan et viverra justo commodo\"\r\n}`,
              disciplineId: 1,
              patientTherapyId: 463,
            },
          ],
        },
        {
          disciplineid: 2,
          history: [
            {
              socEvalDate: "2023-07-01T00:00:00",
              eocDate: null,
              patientDocumentId: 69,
              historyValue: `{\r\n  \"Notes\": \"Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean euismod bibendum laoreet. Proin gravida dolor sit amet lacus accumsan et viverra justo commodo.  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean euismod bibendum laoreet. Proin gravida dolor sit amet lacus accumsan et viverra justo commodo\"\r\n}`,
              disciplineId: 2,
              patientTherapyId: 463,
            },
            {
              socEvalDate: "2023-06-15T00:00:00",
              eocDate: null,
              patientDocumentId: 68,
              historyValue: '{\r\n  "Notes": "Test two"\r\n}',
              disciplineId: 2,
              patientTherapyId: 463,
            },
          ],
        },
        {
          disciplineid: 3,
          history: [
            {
              socEvalDate: "2023-06-15T00:00:00",
              eocDate: null,
              patientDocumentId: 68,
              historyValue: '{\r\n  "Notes": "Test one"\r\n}',
              disciplineId: 3,
              patientTherapyId: 463,
            },
            {
              socEvalDate: "2023-07-01T00:00:00",
              eocDate: null,
              patientDocumentId: 69,
              historyValue: `{\r\n  \"Notes\": \"Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean euismod bibendum laoreet. Proin gravida dolor sit amet lacus accumsan et viverra justo commodo.  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean euismod bibendum laoreet. Proin gravida dolor sit amet lacus accumsan et viverra justo commodo\"\r\n}`,
              disciplineId: 3,
              patientTherapyId: 463,
            },
          ],
        },
      ];
    }
    return [] as SourceData[];
  }

  setQuestionnaire(Questionnaire: Questionnaire | null) {
    this.questionnaire = Questionnaire;
  }

  updateSection(section: Section) {
    this.document?.NavigationSteps?.forEach((step) => {
      step?.Sections?.forEach((s) => {
        if (s.SectionId === section?.SectionId) {
          s = section;
        }
      });
    });
  }

  async updateFormsFieldData(activityID: string, subActivityKey: string, value: any) {
    this.document?.NavigationSteps?.forEach((step) => {
      step?.Sections?.forEach((section) => {
        // code specific to forms page
        if (step.NavigationStepId === "FunctionalLimitationsAndGGSections" && section?.Questionnaire?.length) {
          section.Questionnaire.forEach((question) => {
            question.Activities?.forEach((activity) => {
              if (activity.ActivityId === activityID) {
                const key = this.getValueKey(activity);
                if (activity.Value[key]) {
                  activity.Value[key][subActivityKey] = value;
                } else {
                  activity.Value[key] = { [subActivityKey]: value };
                }
                activity.IsUpdated = true;
              }
            });
          });
        }
      });
    });
  }


  parentHasRequiredDependValues(question: Question, parentQuestionValue: any) {
    const condition = question.ParentQuestionInfo.Condition;

    if (condition === "OR") {
      return question.ParentQuestionInfo?.Value?.some((option) => {
        if (Array.isArray(parentQuestionValue)) {
          return parentQuestionValue.includes(option);
        }
        return parentQuestionValue === option;
      });
    }

    if (condition === "AND") {
      return question.ParentQuestionInfo?.Value?.every((option) => {
        if (Array.isArray(parentQuestionValue)) {
          return parentQuestionValue.includes(option);
        }
        return parentQuestionValue === option;
      });
    }

    if (condition === "ANY") {
      if (Array.isArray(parentQuestionValue)) {
        return parentQuestionValue.length > 0;
      }

      return !!parentQuestionValue;
    }

    if (condition === "ALL") {
      if (Array.isArray(parentQuestionValue)) {
        return (
          parentQuestionValue.length ===
          question.ParentQuestionInfo.Value.length
        );
      }

      return parentQuestionValue === question.ParentQuestionInfo.Value;
    }

    if (condition === "NOT") {
      if (
        parentQuestionValue === null ||
        parentQuestionValue === undefined ||
        (Array.isArray(parentQuestionValue) && parentQuestionValue.length === 0)
      ) {
        return false;
      }

      return !question.ParentQuestionInfo?.Value?.some((option) => {
        if (Array.isArray(parentQuestionValue)) {
          return parentQuestionValue.includes(option);
        }
        return parentQuestionValue === option;
      });
    }

    return false;
  }

  equalsValue(
    parentQuestionInfo: { Value: [] | any },
    question: Question
  ): boolean {
    if (Array.isArray(parentQuestionInfo.Value)) {
      if (Array.isArray(question.Value[Object.keys(question.Value)[0]])) {
        return parentQuestionInfo.Value.some((val) =>
          question.Value[Object.keys(question.Value)[0]].includes(val)
        );
      }
      return parentQuestionInfo.Value.includes(
        question.Value[Object.keys(question.Value)[0]]
      );
    }

    return (
      parentQuestionInfo.Value ===
      question.Value[Object.keys(question.Value)[0]]
    );
  }

  checkForVisibility(conditions: any, questionnaire: Questionnaire) {
    const conditionKey = Object.keys(conditions)[0];
    const conditionsToSatisfy = conditions[conditionKey];
    const shouldVisibile: any[] = [];

    if (conditionKey === 'Any') {
      conditionsToSatisfy.forEach((condition: any) => {
        const fact = condition.Fact;
        const type = condition.Type;

        let valueToCheck: any;

        if (fact.includes('{')) {
          const questionId = fact.substring(1, fact.length - 1);
          const question = this.getQuestion(questionId);
          valueToCheck = this.getValue(question);
        }
        else if (type === 'int') {
          valueToCheck = Number(fact);
        }

        const value = condition.Value;
        const operator = condition.Operator;

        shouldVisibile.push((operator === 'include' && value.includes(valueToCheck)) ||
          (operator === 'equal' && valueToCheck === value))
      })
    }
    return shouldVisibile.filter((visible) => visible === true).length
  }

  /**
   * Generate system prompt summary returns the dynamically answered string
   * @param userDiscipline
   * @param questionnaire
   * @param systemPrompt
   * @returns
   */
  generateSystemPromptSummary(
    userDiscipline: any,
    questionnaire: Questionnaire,
    systemPrompt: string
  ) {
    let summary: string = "";
    this.userDiscipline = userDiscipline;
    const allQuestions = this.getAllQuestions();
    summary = systemPrompt && systemPrompt?.replace(/{([^}]+)}/g, (match, key) => {
      if (key.includes(SummaryPlaceholders.UserDiscipline)) {
        const questionId = key.replace(SummaryPlaceholders.UserDiscipline,  userDiscipline );
        return '<b>' + questionId + '</b>';
      }
      else if (key.includes(SummaryPlaceholders.DisciplineTitle)) {
        const disciplineName =  DisciplineTitle[userDiscipline as keyof typeof DisciplineTitle];
        const questionId = key.replace(SummaryPlaceholders.DisciplineTitle, disciplineName);
        return '<b>' + questionId + '</b>';
      }
      else if (key.includes(SummaryPlaceholders.QuestionId)) {
        const questionId = key.replace(SummaryPlaceholders.QuestionId, "").trim();
        let question = questionnaire.Questions.find(
          (q) => q.QuestionId === questionId
        );
        if (!question) {
          question = allQuestions.find((q) => q.QuestionId === questionId);
        }
        const value = structuredClone(this.getValue(question));
        const displayName = question?.DisplayName;
        if (question?.LookupInfo && this.questionOptions) {
          question.Options = this.questionOptions.find((option: any) => option.question === question?.QuestionId)?.options;
        }
        if (Array.isArray(value)) {
          let replaceValue: any = question?.Options?.filter((o) =>
            value?.includes(o.Id)
          ).map((o) => o.Value);

          if (Array.isArray(replaceValue) && replaceValue.length === 0) {
            replaceValue = null;
          }
          if (replaceValue) {
            let finalText = "";
            replaceValue?.forEach((val: string, index: number) => {
              if (
                index === replaceValue?.length - 1 &&
                replaceValue?.length > 1
              ) {
                finalText += ", and " + val;
              } else {
                finalText += index === 0 ? val : ", " + val;
              }
            });
            return finalText ? '<b>' + finalText + '</b>' : '<b class="text-danger">[' + displayName + ']</b>';
          }
          else {
            if(question?.QuestionType === QuestionTypes.SkilledCare_EnhancedTag){
             let enhancedTagValue = this.GetEnhancedTagValue(question,value);
             return enhancedTagValue ? '<b>' + enhancedTagValue + '</b>' : '<b class="text-danger">[' + displayName + ']</b>';
            }
            for (let i = 0; i < value.length; i++) {
              const val = value[i];

              if (val.includes(':')) {
                const splitValue = val.split(':')[1];
                const replaceValue = question?.Options.find(o => Number(splitValue) === o.Id)?.Value;
                
                value[i] = val.replace(splitValue, replaceValue);

                // remove element if needed
                if (questionId.includes(QuestionIds.SkilledCare_Main_NewApproachesForFocus) && splitValue == 117) {
                  value.splice(i, 1); 
                  i--;               
                  continue;          
                }

                if (questionId.includes(QuestionIds.PurposeOfTreatmentValues)) {
                  value[i] = value[i].replace(":", " ");
                } else {
                  value[i] = value[i].replace(":", " with ");
                }
              }
            }
          }
        } else if (question?.Options && question?.Options?.length > 0 && value) {
          const getValueFromOptions = (question?.Options?.find(
            (o: any) => (o.Id && o.Id === value) || (o.id && o.id === value)
          )) as any;
          const replaceValue = getValueFromOptions?.Id ? getValueFromOptions?.Value : getValueFromOptions?.name;
          return replaceValue ? '<b>' + replaceValue + '</b>' : '<b class="text-danger">[' + displayName + ']</b>';
        }
        if (questionId.includes("NewApproachesForFocus") &&  (value == null || value == undefined || value.length === 0)) {
          return '';
        }
        else if(Array.isArray(value)){
          return value.length ? '<b>' + value + '</b>' : '<b class="text-danger">[' + displayName + ']</b>';
        }
        return value ? '<b>' + value + '</b>' : '<b class="text-danger">[' + displayName + ']</b>';
      }
      else if (key.includes(SummaryPlaceholders.Endpoint)) {
        const EndpointName = key.replace(SummaryPlaceholders.Endpoint, "").replace(":", "").trim();
        if(this.endpointsStore?.[EndpointName]?.includes('{')){
          const generatedSummary = this.generateSystemPromptSummary(userDiscipline, questionnaire, this.endpointsStore[EndpointName])
          return generatedSummary;
        }
        else {
          return this.endpointsStore?.[EndpointName] ? '<b>' + this.endpointsStore?.[EndpointName] + '</b>' : '<b class="text-danger">[' + EndpointName + ']</b>'
        }
      }
      return (this.summaryInfo as any)[key.trim()] || "";
    });

    // remove cases where a period is followed by spaces and another period
    summary = summary.replace(/\.\s*\./g, '.');

    // remove double spaces if any were left
    summary = summary.replace(/\s{2,}/g, ' ');


    if (summary?.length === 1) {
      return "";
    }
    return summary;
  }

  generateSummary(questionnaire: Questionnaire, viewOnlyMode: boolean = false) {
    let summary: string = "";
    questionnaire?.Value?.DependencyQuestionInfo?.forEach((question) => {
      const allQuestions = this.getAllQuestions();

      let dependentQuestion: Question | undefined;
      let dependentExists: boolean = false;
      questionnaire.Questions.forEach((questionnaireQues) => {
        if (questionnaireQues.QuestionId == question.Id) {
          dependentExists = true;
          dependentQuestion = questionnaireQues
        }
      })

      if (!dependentExists) {
        dependentQuestion = allQuestions?.find((q) => q.QuestionId === question.Id);
        dependentExists = false;
      }

      let dependentQuestionValue;

      dependentQuestionValue = this.getValue(dependentQuestion);

      if (!dependentQuestionValue) {
        if (question.CheckDefault) {
          dependentQuestionValue = dependentQuestion?.DefaultValue?.Value;
        }
        else {
          return;
        }
      }
      const matchingQuestion = questionnaire.Questions.find((questionnaireQues) =>
        questionnaireQues.QuestionId == dependentQuestion?.QuestionId && questionnaireQues.QuestionType == 'ComplexGrid'
      );

      if (matchingQuestion) {
        summary = this.getComplexGridSummary(question, dependentQuestion, matchingQuestion, summary);
      }


      if (questionnaire.Value.Condition === "OR") {
        summary = this.getBaseSummary(
          question,
          dependentQuestionValue,
          summary
        );

        if (question.Append?.length) {
          summary = this.getAppend(question, allQuestions || [], summary);
        }
      } else {
        summary = this.getBaseSummary(
          question,
          dependentQuestionValue,
          summary
        );

        if (question.Append?.length) {
          summary = this.getAppend(question, allQuestions || [], summary);
        }
      }
    });
    this.getAllQuestions();
    summary = summary.replace(/{([^}]+)}/g, (match, key) => {
      if (match.includes("QuestionId: ")) {
        const questionId = match
          .replace("QuestionId: ", "")
          .replace("}", "")
          .replace("{", "")
          .trim();
        let question = questionnaire.Questions.find(
          (q) => q.QuestionId === questionId
        );
        if (!question) {
          question = this.allQuestions.find(
            (q) => q.QuestionId === questionId
          );
        }
        const value = this.getValue(question);
        let displayName = question?.DisplayName;
        if (questionId === 'WhoProvidedAssistance' && !displayName && questionnaire.QuestionnaireId.includes('DischargeEnvironmentalAndPlan')) {
          displayName = 'Who will provide the assistance?'
        }
        if (question && question?.LookupInfo && this.questionOptions) {
          question.Options = this.questionOptions.find((option: any) => option.question === question?.QuestionId)?.options;
        }
        if (questionId === Constants.PriorHospitalizationToDateQuestionId) {
          const notApplicableQuestion = questionnaire.Questions.find(
            (q) => q.QuestionId === Constants.PriorHospitalizationNotApplicableQuestionId
          ) || this.allQuestions.find(
            (q) => q.QuestionId === Constants.PriorHospitalizationNotApplicableQuestionId
          );
          if (this.getValue(notApplicableQuestion)) {
            return Constants.NotApplicableSummaryText;
          }
          return value ? value : '<b class="text-danger">[' + displayName + ']</b>';
        }
        if (Array.isArray(value)) {
          const replaceValue = question?.Options?.filter((o) =>
            value?.includes(o.Id)
          ).map((o) => o.Value);

          let finalText = "";
          replaceValue?.forEach((val, index) => {
            if (
              index === replaceValue?.length - 1 &&
              replaceValue?.length > 1
            ) {
              if (viewOnlyMode)
                finalText += "<b>, and</b><span contenteditable=\"false\">&nbsp;</span>" + "<b>" + val + "</b>";
              else
                finalText += "<b>, and</b><span contenteditable=\"plaintext-only\">&nbsp;</span>" + "<b>" + val + "</b>";
            } else {
              if (viewOnlyMode)
                finalText += index === 0 ? "<b>" + val + "</b>" : "<b>,</b><span contenteditable=\"false\">&nbsp;</span>" + "<b>" + val + "</b>";
              else
                finalText += index === 0 ? "<b>" + val + "</b>" : "<b>,</b><span contenteditable=\"plaintext-only\">&nbsp;</span>" + "<b>" + val + "</b>";
            }
          });

          return finalText
            ? finalText
            : '<b class="text-danger">[' + displayName + "]</b>";
        } else if (question?.Options) {
          const getValueFromOptions = (question?.Options?.find(
            (o: any) => (o.Id && o.Id === value) || (o.id && o.id === value)
          )) as any;
          const replaceValue = getValueFromOptions?.Id ? getValueFromOptions?.Value : getValueFromOptions?.name;
          return replaceValue ? '<b>' + replaceValue + '</b>' : '<b class="text-danger">[' + displayName + ']</b>';
        }

        return value ? '<b>' + value + '</b>' : '<b class="text-danger">[' + displayName + ']</b>';
      }
      else if (match.includes("Endpoint")) {
        const EndpointName = match
          .replace("Endpoint : ", "")
          .replace("}", "")
          .replace("{", "")
          .trim();
        return this.endpointsStore[EndpointName] ? '<b>' + this.endpointsStore[EndpointName] + '</b>' : '<b class="text-danger">[' + EndpointName + ']</b>'
      }
      else if (match.includes('MultiDependencyDetails')) {
        const multiDependent = match
          .replace("MultiDependencyDetails: ", "")
          .replace("}", "")
          .replace("{", "")
          .trim();
        return this.multiDependencyStore[multiDependent] ? '<b>' + this.multiDependencyStore[multiDependent] + '</b>' : '<b class="text-danger">[' + multiDependent + ']</b>'
      }
      return '<b>' + (this.summaryInfo as any)[key.trim()] + '</b>' || "";
    });

    if (summary?.length === 1) {
      return "";
    }

    return summary;
  }

  async getEndpointsStore(questionnaire: Questionnaire, question?: Question) {
    let endpoints = [];
    if (question) {
      endpoints = question.ValueReplaceConfig.Endpoint;
    }
    else {
      endpoints = questionnaire.Value.Endpoint;
    }
    for (const endpoint of endpoints) {
      const EndpointName = endpoint.Name
      if (endpoint) {
        let params: any = {};
        let parameters: Parameter[] = [];
        if (endpoint?.LookupInfo.EndpointInfo.Type.toLowerCase() === "get") {
          parameters = endpoint?.LookupInfo.EndpointInfo?.Parameters;
        } else {
          if (endpoint?.LookupInfo.EndpointInfo?.Parameters?.[0].Object) {
            parameters = endpoint.LookupInfo.EndpointInfo?.Parameters?.[0].Object;
          } else {
            parameters = endpoint.LookupInfo.EndpointInfo?.Parameters;
          }
        }
        parameters?.forEach((param) => {
          if (param.Value !== null && param.Value !== undefined) {
            if (typeof param.Value === "string" && param.Value.startsWith("{")) {
              const fieldId = param.Value.substring(1, param.Value.length - 1);
              let fieldIdToPass = this.document?.NavigationSteps.map(
                (step) => step.Sections
              )
                .flat()
                .map((section) => section?.Fields)
                .flat()
                .find((field) => field?.FieldId === fieldId)?.Value;

              if (!fieldIdToPass) {
                fieldIdToPass = this.document?.NavigationSteps.map(
                  (step) => step.Sections
                )
                  .flat()
                  .map((section) => section?.Questionnaire)
                  .flat()
                  .map((questionnaire) => questionnaire?.Questions)
                  .flat()
                  .find((field) => field?.QuestionId === fieldId)?.Value;
              }

              const valueKey = Object.keys(fieldIdToPass || {})?.[0];

              if (valueKey) {
                const value = fieldIdToPass?.[valueKey];

                if (!value) {
                  return;
                }
                params[param.ParameterName] = value;
              }
            } else {
              params[param.ParameterName] = param.Value;
            }
          }
        });
        this.endpointsStore[EndpointName] = await this.getReplaceSummaryValue(endpoint.LookupInfo, params);
      }
    }
  }

  async getMultiDependencyValue(questionnaire: Questionnaire, viewOnlyMode: boolean = false) {
    const dependencyConfigurations = questionnaire.Value.MultiDependencyDetails;
    dependencyConfigurations?.forEach((configuration) => {
      const configurationName = configuration.Name;
      const configs = configuration.Configurations;
      const dependentConfig = configs.DependentQuestionId;
      const dependentConfigQuestion = this.getQuestion(dependentConfig);
      const dependentQuestionValue = this.getValue(dependentConfigQuestion);
      const dependentValues = configs.ValueConfigurations;
      dependentValues?.forEach((configvalue) => {
        if (configvalue?.Key?.toString() === dependentQuestionValue?.toString()) {
          const question = this.getQuestion(configvalue.ValueQuestionId)
          const value = this.getValue(question);
          let multiDependentValue;
          if (question && question?.LookupInfo && this.questionOptions) {
            question.Options = this.questionOptions.find((option: any) => option.question === question?.QuestionId)?.options;
          }
          if (Array.isArray(value)) {
            const replaceValue = question?.Options?.filter((o) =>
              value?.includes(o.Id)
            ).map((o) => o.Value);

            let finalText = "";
            replaceValue?.forEach((val, index) => {
              if (
                index === replaceValue?.length - 1 &&
                replaceValue?.length > 1
              ) {
                if (viewOnlyMode)
                  finalText += "<b>, and</b><span contenteditable=\"false\">&nbsp;</span>" + "<b>" + val + "</b>";
                else
                  finalText += "<b>, and</b><span contenteditable=\"plaintext-only\">&nbsp;</span>" + "<b>" + val + "</b>";
              } else {
                if (viewOnlyMode)
                  finalText += index === 0 ? "<b>" + val + "</b>" : "<b>,</b><span contenteditable=\"false\">&nbsp;</span>" + "<b>" + val + "</b>";
                else
                  finalText += index === 0 ? "<b>" + val + "</b>" : "<b>,</b><span contenteditable=\"plaintext-only\">&nbsp;</span>" + "<b>" + val + "</b>";
              }
            });

            multiDependentValue = finalText;
          } else if (question?.Options) {
            const getValueFromOptions = (question?.Options?.find(
              (o: any) => (o.Id && o.Id === value) || (o.id && o.id === value)
            )) as any;
            multiDependentValue = getValueFromOptions?.Id ? getValueFromOptions?.Value : getValueFromOptions?.name;
          }
          this.multiDependencyStore[configurationName] = multiDependentValue;
        }
      })
    })
  }

  async getReplaceSummaryValue(endpoint: Lookup, params: any): Promise<string> {
    if (endpoint.EndpointInfo.Type.toLowerCase() === 'get') {
      let url = `${environment.endpoint}/${endpoint.EndpointInfo.Url}`;

      return (await firstValueFrom(this.httpClient
        .get<{ data?: string; responseData: string }>(url, {
          params: params,
        })
        .pipe(
          map((response) => {
            if (response.data) {
              return response.data;
            }
            return response.responseData;
          })
        )));
    }
    return (await firstValueFrom(this.httpClient
      .post<{ responseData: string }>(
        `${environment.endpoint}/${endpoint.EndpointInfo.Url}`,
        params
      ).pipe(map((response) => response.responseData))))
  }

  getComplexGridSummary(
    question: any, dependentQuestion: any, questionnaireQues: Question, previousSummary: string) {
    let summary = '';
    let summaryLivedWith = '';
    let summaryComplex = '';
    const lengthOfOptions = questionnaireQues?.Options.length;
    if (lengthOfOptions) {
      const dependents: any = questionnaireQues?.Options[lengthOfOptions - 1];
      const dependentKeys = Object.keys(dependents);
      let dependentValue = dependents[dependentKeys[0]];
      dependentValue = dependentValue?.replace("{", "").replace("}", "");
      const dependentQuestionId = dependentValue?.split(":")[1].trim();

      let preValuesOptions: any[] = [];

      const matchingQuestion = this.questionnaire?.Questions?.find((ques) => ques.QuestionId == dependentQuestionId);

      if (matchingQuestion) {
        const preValues: any = Object.values(matchingQuestion.Value)[0];
        preValues?.forEach((selectedVal: number) => {
          matchingQuestion.Options?.forEach((option: any, index) => {
            if (option.id === selectedVal || option.Id === selectedVal) {
              preValuesOptions[index] = option.name || option.Value;
            }
          });
        });
      }

      preValuesOptions = preValuesOptions.filter(value => value != null).reverse();

      const dependentQuestionValues: any = Object.values(dependentQuestion.Value)[0]

      dependentQuestionValues?.forEach((val: any, index: number) => {
        if (index > dependentQuestionValues.length - lengthOfOptions) {
          let complexPreSummary = question.Options[0].Summary;
          if (val[dependentKeys[0]]) {
            const whoAssist: any = questionnaireQues?.Options[index - preValuesOptions.length];
            complexPreSummary = complexPreSummary.replace(/{([^}]+)}/g, (match: string, _key: any) => {
              if (match.includes("QuestionId: ")) {
                const questionId = match
                  .replace("QuestionId: ", "")
                  .replace("}", "")
                  .replace("{", "")
                  .trim();
                if (questionId == dependentKeys[0] && whoAssist) {
                  return '<b>' + whoAssist[dependentKeys[0]] + '</b>'
                }
                else if (questionId == dependentKeys[1]) {
                  return '<b>' + val[dependentKeys[1]] + '</b>'
                }
                else if (questionId == dependentKeys[2]) {
                  return '<b>' + val[dependentKeys[2]] + '</b>'
                }
              }
              return '';
            })
            summaryComplex += complexPreSummary;
          }
        } else {
          if (val[dependentKeys[0]]) {
            if (summaryLivedWith.length) {
              summaryLivedWith += ', '
            }
            summaryLivedWith += '<b>' + preValuesOptions[index] + '</b>';
          }
        }
      });
    }
    if (summaryLivedWith.length) {
      previousSummary = previousSummary.replace(/{([^}]+)}/g, summaryLivedWith);
      summary = previousSummary + summaryComplex;
    }
    else {
      summary = summaryComplex;
    }
    return summary;
  }

  public getAllQuestions() {

    this.allQuestions =
      this.document?.NavigationSteps?.flatMap((step) =>
        step.Sections?.flatMap((section) => section.Questionnaire)?.flatMap(
          (quest) => quest?.Questions
        )
      ).filter((x) => !!x) || [];

    return this.allQuestions;
  }

  private getAppend(
    question: {
      Id: string;
      Append: {
        Condition: SummaryCondition;
        Id: string;
        SelectedValue: any[];
        Text: string;
      }[];
      Options: {
        SelectedValue: any[];
        Condition: SummaryCondition;
        Summary: string;
      }[];
    },
    allQuestions: Question[],
    summary: string
  ) {
    question.Append?.forEach((append) => {
      const dependentQuestion = allQuestions?.find((q) => q.QuestionId === append.Id);
      const dependentQuestionValue = this.getValue(
        allQuestions?.find((q) => q.QuestionId === append.Id)
      );
      if (
        append.SelectedValue?.some((value) =>
          Array.isArray(dependentQuestionValue)
            ? dependentQuestionValue?.includes(value)
            : dependentQuestionValue === value
        ) &&
        append.Condition === "OR"
      ) {
        summary += append.Text;
      }

      if (
        append.SelectedValue?.every((val: any) =>
          Array.isArray(dependentQuestionValue)
            ? dependentQuestionValue?.includes(val)
            : dependentQuestionValue === val
        ) &&
        append.Condition === "AND"
      ) {
        summary += append.Text;
      }

      if (
        append.Condition === "ANY" &&
        dependentQuestionValue !== null &&
        dependentQuestionValue !== undefined &&
        (!Array.isArray(dependentQuestionValue) || (Array.isArray(dependentQuestionValue) && dependentQuestionValue.length > 0))
      ) {
        summary += append.Text;
      }

      if (
        append.Condition === "ALL" &&
        dependentQuestionValue !== null &&
        append.SelectedValue?.every((val: any) =>
          Array.isArray(dependentQuestionValue)
            ? dependentQuestionValue?.includes(val)
            : dependentQuestionValue === val
        )
      ) {
        summary += append.Text;
      }
    });

    return summary;
  }

  private getBaseSummary(
    question: {
      Id: string;
      Append: {
        Condition: SummaryCondition;
        Id: string;
        SelectedValue: any[];
        Text: string;
      }[];
      Options: {
        SelectedValue: any[];
        Condition: SummaryCondition;
        Summary: string;
      }[];
    },
    dependentQuestionValue: any,
    summary: string
  ) {
    question.Options.forEach((option) => {
      if (
        option.Condition === "OR" &&
        option.SelectedValue.some((value) =>
          Array.isArray(dependentQuestionValue)
            ? dependentQuestionValue?.includes(value)
            : dependentQuestionValue === value
        )
      ) {
        summary += option.Summary;
      }

      if (
        option.Condition === "AND" &&
        option.SelectedValue.every((val: any) =>
          Array.isArray(dependentQuestionValue)
            ? dependentQuestionValue?.includes(val)
            : dependentQuestionValue === val
        )
      ) {
        summary += option.Summary;
      }

      if (
        option.Condition === "ANY" &&
        dependentQuestionValue !== null &&
        dependentQuestionValue !== undefined
      ) {
        summary += option.Summary;
      }

      if (
        option.Condition === "ALL" &&
        dependentQuestionValue !== null &&
        option.SelectedValue.every((val: any) =>
          Array.isArray(dependentQuestionValue)
            ? dependentQuestionValue?.includes(val)
            : dependentQuestionValue === val
        )
      ) {
        summary += option.Summary;
      }
    });
    return summary;
  }

  async updateQuestionnaire(
    Questionnaire: Questionnaire,
    shouldGenerateSummary = true, AISummary?: string | null, isViewOnlyMode: boolean = false
  ) {
    this.questionnaire = Questionnaire;
    this.document?.NavigationSteps?.forEach((step) => {
      step.Sections?.forEach((section) => {
        const questionnaireIndex = section.Questionnaire?.findIndex(
          (q) => q.QuestionnaireId === Questionnaire?.QuestionnaireId
        );

        if (questionnaireIndex !== undefined && questionnaireIndex !== -1) {
          section.Questionnaire[questionnaireIndex] = Questionnaire;
          section.Questionnaire[questionnaireIndex].IsUpdated = true;
          section.IsUpdated = true;
          let summary = shouldGenerateSummary
            ? Questionnaire?.Value?.PreText ?
              this.generateSystemPromptSummary(this.userDiscipline, Questionnaire, Questionnaire.Value.PreText) + this.generateSummary(Questionnaire, isViewOnlyMode) : this.generateSummary(Questionnaire, isViewOnlyMode)
            : AISummary || Questionnaire.Value?.Summary;
          // Allowed HTML tags with attributes allowedTags = ["p", "div", "span", "b", "br"];
          // Escape standalone < and > but allow specific HTML tags with attributes
          summary = summary?.replace(/<(?!\/?(p|div|span|b|br)(\s+[^<>]*)?>)/g, '&lt;')
            .replace(/(?<!<\/?(p|div|span|b|br)(\s+[^<>]*)?)>/g, '&gt;');
          // Replace standalone '&' (but keep valid HTML entities like &lt;, &nbsp;)
          summary = summary?.replace(/&(?![a-zA-Z0-9#]+;)/g, '&amp;');

            // Replace multiple spaces (2 or more) with '&nbsp;' to preserve formatting
            summary = summary?.replace(/ {2,}/g, (match: string) => match.split('').map(() => '&nbsp;').join(''));
            
            const overwriteSummary = this.getOverwriteSummaryValue(Questionnaire);
            if (overwriteSummary) {
              summary = overwriteSummary;
            }

          if (section.Questionnaire![questionnaireIndex].Value) {
            section.Questionnaire![questionnaireIndex].Value!["Summary"] = summary;
            section.Value = summary;
          }
          this.updateDependentQuestions(this.questionnaire as Questionnaire);
        }
      });
    });
    const navigationStep = this.document?.NavigationSteps?.find((step) =>
      step.Sections?.some(
        (section) =>
          section.Questionnaire?.some(
            (q) => q.QuestionnaireId === Questionnaire?.QuestionnaireId
          )
      )
    ) || ({} as NavigationStep);

    navigationStep.IsUpdated = true;
  }

  getValue(question: any) {
    const key = this.getValueKey(question);

    if (!key) return null;

    const value = question?.Value[key];

    return value;
  }

  setValue(question: any, value: any) {
    const key = this.getValueKey(question);
    if (!key) return;

    question.Value[key] = value;
  }

  getValueKey(question: any) {
    const valueKey = Object.keys(question?.Value || {})?.filter(
      (x) => x !== "ChildValues" && x !== "Increments"
    )[0];

    return valueKey || "";
  }

  evaluate(fact: any, value: any, operator: Operator): boolean {
    const parseDate = (input: any): Date | null => {
      if (typeof input === "string" || input instanceof Date) {
        const date = new Date(input);
        return isNaN(date.getTime()) ? null : date;
      }
      return null;
    };

    const factDate = parseDate(fact);
    const valueDate = parseDate(value);

    switch (operator) {
      case "equal":
        return fact === value;
      case "notEqual":
        return fact !== value;
      case "lessThan":
        if (factDate && valueDate) {
          return factDate < valueDate;
        }
        return fact < value;
      case "lessThanInclusive":
        if (factDate && valueDate) {
          return factDate <= valueDate;
        }
        return fact <= value;
      case "greaterThan":
        if (factDate && valueDate) {
          return factDate > valueDate;
        }
        return fact > value;
      case "greaterThanInclusive":
        if (factDate && valueDate) {
          return factDate >= valueDate;
        }
        return fact >= value;
      case "in":
        return Array.isArray(value) && value.includes(fact);
      case "notIn":
        return Array.isArray(value) && !value.includes(fact);
      case "contains":
        return Array.isArray(fact) && fact.includes(value);
      case "doesNotContain":
        return Array.isArray(fact) && !fact.includes(value);
      default:
        throw new Error(`Unsupported operator: ${operator}`);
    }
  }

  clear() {
    this.composeSection = {} as Section;
    this.questionnaire = {} as Questionnaire;
    this.document = null;
    this.documentMetadata = {};
    this.summaryInfo = {};
    this.currentStep = {} as NavigationStep;
    this.allQuestions = [];
    this.questionOptions = [];
  }


  getQuestion(questionId: string) {
    return this.document?.NavigationSteps?.flatMap((step) =>
      step.Sections?.flatMap((section) => section.Questionnaire)?.flatMap(
        (quest) => quest?.Questions
      )
    ).find((q) => q?.QuestionId === questionId);
  }

  updateQuestionInEpocService(questionnaire: Questionnaire, question: Question) {
    if (!questionnaire?.QuestionnaireId || !Array.isArray(questionnaire.Questions)) {
      return;
    }
  
    const index = questionnaire.Questions.findIndex(
      q => q?.QuestionId === question.QuestionId
    );
  
    if (index !== -1) {
      questionnaire.Questions[index] = question;
    }
  }

  getOtions(questionId: string) {
    return this.questionnaire?.Questions?.find((q) => q?.QuestionId === questionId)?.Options;
  }

  updateDependentQuestions(questionnaire: Questionnaire) {
    const clearedQuestions: Question[] = [];
    const updatedQuestions: Question[] = [];
    const questionsNotInQuestionniare = this.allQuestions.filter((q) =>
      questionnaire.Questions?.every(
        (quest) => quest.QuestionId !== q.QuestionId
      )
    );
    questionnaire?.Questions?.forEach((question: Question) => {
      const questionsThatDependsOnThis = questionsNotInQuestionniare.filter(
        (q) => q.ParentQuestionInfo?.Id === question.QuestionId
      );

      if (questionsThatDependsOnThis?.length) {
        questionsThatDependsOnThis.forEach((q) => {
          if (this.getValue(q) && !q.QuestionId.includes('MedicalInformation_CurrentMedicalHistory/ComplexitiesAndPMH_Compose')) {
            this.setValue(q, null);
            q.IsUpdated = true;
            clearedQuestions.push(q);
          }
        });
      }
    });

    questionsNotInQuestionniare?.forEach((q) => {
      if (q.DefaultValue?.DependencyQuestionInfo?.length) {
        q.DefaultValue.DependencyQuestionInfo.forEach((dependency) => {
          const parentQuestion = this.getQuestion(dependency.Id) as any;
          if (
            parentQuestion &&
            this.parentHasRequiredDependValues(
              {
                ParentQuestionInfo: {
                  Condition: dependency.Options[0].Condition,
                  Value: dependency.Options[0].SelectedValue,
                },
              } as any,
              this.getValue(parentQuestion)
            )
          ) {
            const questionIdToGetValueFrom =
              dependency?.Options?.[0]?.Value.substring(
                1,
                dependency?.Options?.[0]?.Value.lastIndexOf("}")
              );

            const questionIsInCurrentQuestionnaire =
              questionnaire.Questions?.find(
                (quest) => quest.QuestionId === questionIdToGetValueFrom
              );

            if (questionIsInCurrentQuestionnaire && this.getValue(questionIsInCurrentQuestionnaire)) {
              const value = JSON.parse(JSON.stringify(this.getValue(questionIsInCurrentQuestionnaire)));
              if (q.QuestionType === "ComplexGrid" && !value?.length) {
                value?.forEach((v: any) => {
                  const updatedObject: any = {};
                  Object.keys(v).forEach((key, index) => {
                    const oldKey = key;
                    const value = v[key];
                    const replacementKey = questionIsInCurrentQuestionnaire.GridConfiguration?.Columns[
                      index
                    ]?.PropertyName as string;

                    updatedObject[replacementKey] = value;
                  });
                  v = updatedObject;
                });
                this.setValue(q, value);
                q.IsUpdated = true;
              }
            }
          }
        });
      }
    });

    const clearedQuestionnaires = clearedQuestions?.map((q) => {
      return this.document?.NavigationSteps?.flatMap((step) =>
        step.Sections?.flatMap((section) => section.Questionnaire)
      ).find((quest) => quest?.Questions?.find((question) => question.QuestionId === q.QuestionId));
    });

    clearedQuestionnaires
      ?.filter((x) => !!x)
      ?.forEach((quest: any) => {
        quest.Value.Summary = null;
        quest.IsUpdated = true;
      });

    const updatedQuestionnaires: any = updatedQuestions?.map((q) => {
      return this.document?.NavigationSteps?.flatMap((step) =>
        step.Sections?.flatMap((section) => section.Questionnaire)
      ).find((quest) => quest?.Questions?.find((question) => question.QuestionId === q.QuestionId));
    });

    updatedQuestionnaires
      ?.filter((x: any) => !!x)
      ?.forEach((quest: any) => {
        quest.Value.Summary = this.generateSummary(quest);
        quest.IsUpdated = true;
      });
  }

  triggerSummaryDependencies(question: Question, viewOnlyMode: boolean = false, visited: Set<string> = new Set()) {
    const summaryDependencies = question.Dependencies?.SummaryDependency;
    if (!summaryDependencies?.length) return;

    summaryDependencies.forEach((dependency) => {
      if (visited.has(dependency.SummaryQuestionnaireId)) return;

      if (dependency.Condition) {
        const gateQuestion = this.getQuestion(dependency.Condition.Id);
        const gateSatisfied = this.parentHasRequiredDependValues(
          { ParentQuestionInfo: dependency.Condition } as any,
          this.getValue(gateQuestion)
        );
        if (!gateSatisfied) return;
      }

      visited.add(dependency.SummaryQuestionnaireId);

      const targetQuestionnaire = this.document?.NavigationSteps
        ?.flatMap((step) => step.Sections)
        ?.flatMap((section) => section?.Questionnaire)
        ?.find((q) => q?.QuestionnaireId === dependency.SummaryQuestionnaireId);

      if (!targetQuestionnaire) return;

      if (dependency.Action === SUMMARY_DEPENDENCY_ACTION.CLEAR) {
        this.clearQuestionnaireSummary(targetQuestionnaire);
      } else {
        this.updateQuestionnaire(targetQuestionnaire, true, null, viewOnlyMode);
        targetQuestionnaire.Questions?.forEach((q) => this.triggerSummaryDependencies(q, viewOnlyMode, visited));
      }
    });
  }

  private clearQuestionnaireSummary(questionnaire: Questionnaire) {
    this.document?.NavigationSteps?.forEach((step) => {
      let matched = false;
      step.Sections?.forEach((section) => {
        const questionnaireIndex = section.Questionnaire?.findIndex(
          (q) => q.QuestionnaireId === questionnaire.QuestionnaireId
        );
        if (questionnaireIndex !== undefined && questionnaireIndex !== -1) {
          section.Questionnaire[questionnaireIndex].Value.Summary = null;
          section.Questionnaire[questionnaireIndex].IsUpdated = true;
          section.Value = null;
          section.IsUpdated = true;
          matched = true;
        }
      });
      if (matched) {
        step.IsUpdated = true;
      }
    });
  }


  clearGoalsData() {
    const underlyingImpairmentsSection = this.document?.NavigationSteps?.find((step) => step.NavigationStepId === NavigationSteps.UnderlyingImpairments)
      ?.Sections.find((section) => section?.SectionId === DisabledSectionIdsForEvalutionOnly.UnderlyingImpairments);

    const functionalLimitationsSection = this.document?.NavigationSteps.find((step) => step?.NavigationStepId === NavigationSteps.FunctionalLimitations)
      ?.Sections.find((section) => section?.SectionId === DisabledSectionIdsForEvalutionOnly.FunctionalLimitations);

    const functionalLimitationsQuestionnaire = functionalLimitationsSection?.Questionnaire?.find(questionnaire => true);

    const underlyingImpairmentsData = this.getValue(underlyingImpairmentsSection);
    const functionalLimitationsData = this.getValue(functionalLimitationsQuestionnaire);

    this.validateAndRemoveGoals(underlyingImpairmentsSection, underlyingImpairmentsData);
    this.validateAndRemoveGoals(functionalLimitationsQuestionnaire, functionalLimitationsData);
  }

  syncMissingDefaultDependencyValues(): void {
    try {
      const allQuestions = this.getAllQuestions();

      allQuestions.forEach((targetQuestion) => {
        try {
          this.syncMissingDefaultDependencyValueForQuestion(
            targetQuestion,
            allQuestions
          );
        } catch (error) {
          console.warn(
            `Failed to sync default dependency value for question ${targetQuestion?.QuestionId}`,
            error
          );
        }
      });
    } catch (error) {
      console.warn("Failed to sync missing default dependency values", error);
    }
  }

  private validateAndRemoveGoals(section: any, goalsData: any[]) {
    if (goalsData?.length) {
      goalsData.forEach((goal: any) => {
        if (goal?.Goals?.length) {
          if (this.patientDocumentClient.touchedObject[goal?.Area + goal?.Subcategory]) {
            delete this.patientDocumentClient.touchedObject[goal?.Area + goal?.Subcategory];
          }
          goal.Goals = [];
        }
      });
      this.setValue(section, goalsData);
    }
  }

  private GetEnhancedTagValue(question: Question, value: any[]): string {
    if (!Array.isArray(value) || value.length === 0) {
      return AppConstants.emptyString;
    }

    if(!question?.SubOptions){
      return AppConstants.emptyString;
    }
  
    const loaMap = new Map<number, string>(
      question.SubOptions.map((loa :any) => [loa.id, loa.name])
    );
  
    return value
      .map(item => {
        const loaName = loaMap.get(item.loaId);
        return loaName ? `${loaName} ${item.interventionName}` : item.interventionName;
      })
      .join(', ');
  }

  private getOverwriteSummaryValue(questionnaire: Questionnaire): string | undefined {
    const overwriteQuestion = questionnaire?.Questions
      ?.filter((q: any) => q?.OverwriteSummary === true)
      ?.find((q: Question) => {
        if (!this.isQuestionVisibleForSummaryOverwrite(q, questionnaire)) {
          return false;
        }

        const value = this.getValue(q);
        return value !== null && value !== undefined && value !== '';
      });

    if (!overwriteQuestion) return undefined;

    const value = this.getValue(overwriteQuestion);
    return typeof value === 'string' ? value : undefined;
  }

  private isQuestionVisibleForSummaryOverwrite(
    question: Question,
    questionnaire: Questionnaire,
    visitedQuestionIds: Set<string> = new Set<string>()
  ): boolean {
    if (!question || question.HideQuestion) {
      return false;
    }

    if (visitedQuestionIds.has(question.QuestionId)) {
      return true;
    }

    visitedQuestionIds.add(question.QuestionId);

    if (question.IsVisible?.Conditions) {
      const shouldBeVisible = this.checkForVisibility(
        question.IsVisible.Conditions,
        questionnaire
      );

      if (!shouldBeVisible) {
        return false;
      }
    }

    const parentQuestionId = question.ParentQuestionInfo?.Id;
    if (!parentQuestionId) {
      return true;
    }

    const parentQuestion =
      questionnaire.Questions?.find((q) => q.QuestionId === parentQuestionId) ||
      this.getQuestion(parentQuestionId);

    if (!parentQuestion) {
      return false;
    }

    const parentHasRequiredValue = this.parentHasRequiredDependValues(
      question,
      this.getValue(parentQuestion)
    );

    if (!parentHasRequiredValue) {
      return false;
    }

    return this.isQuestionVisibleForSummaryOverwrite(
      parentQuestion,
      questionnaire,
      visitedQuestionIds
    );
  }

  private syncMissingDefaultDependencyValueForQuestion(
    targetQuestion: Question,
    allQuestions: Question[]
  ): void {
    const dependencyInfos = targetQuestion?.DefaultValue?.DependencyQuestionInfo;

    if (!dependencyInfos?.length) {
      return;
    }

    const targetValue = this.getValue(targetQuestion);

    if (this.hasValue(targetValue)) {
      return;
    }

    for (const dependencyInfo of dependencyInfos) {
      const parentQuestion = allQuestions.find(
        (question) => question.QuestionId === dependencyInfo.Id
      );

      if (!parentQuestion) {
        continue;
      }

      const parentValue = this.getValue(parentQuestion);

      const matchingOption = dependencyInfo.Options?.find((option: any) =>
        this.parentHasRequiredDependValues(
          {
            ParentQuestionInfo: {
              Condition: option.Condition,
              Value: option.SelectedValue,
            },
          } as Question,
          parentValue
        )
      );

      if (!matchingOption?.Value) {
        continue;
      }

      const sourceQuestionId = this.extractQuestionIdFromDefaultValue(
        matchingOption.Value
      );

      if (!sourceQuestionId) {
        continue;
      }

      const sourceQuestion = allQuestions.find(
        (question) => question.QuestionId === sourceQuestionId
      );

      if (!sourceQuestion) {
        continue;
      }

      const sourceValue = this.getValue(sourceQuestion);

      if (!this.hasValue(sourceValue)) {
        continue;
      }

      this.setValue(targetQuestion, this.cloneValue(sourceValue));
      targetQuestion.IsUpdated = true;

      return;
    }
  }

  private hasValue(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === "string") {
      return value.trim().length > 0;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return true;
  }

  private cloneValue(value: any): any {
    return JSON.parse(JSON.stringify(value));
  }

  private extractQuestionIdFromDefaultValue(value: string): string | null {
    if (!value) {
      return null;
    }

    const match = value.match(/\{([^}]+)\}/);

    return match?.[1]?.trim() || null;
  }
}
